import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link, useParams } from "react-router-dom";
import { poolInvitationService, type ValidateInvitationResponseDTO } from "@/frontend/services";
import { useAuthStore } from "@/frontend/stores";
import { Button, Spin, Result, message } from "antd";
import { CheckCircleOutlined, UserAddOutlined, LoadingOutlined, CloseCircleOutlined } from "@ant-design/icons";

type PageState = "loading" | "valid" | "error" | "accepting" | "success";

export function JoinPoolPage() {
  const [searchParams] = useSearchParams();
  const { token: routeToken } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const token = searchParams.get("token") || routeToken || undefined;
  const source = searchParams.get("source");
  const appDeepLinkBase = searchParams.get("appDeepLinkBase") || "worldcupadp://join";
  const { isAuthenticated, user } = useAuthStore();
  const appJoinUrl = token ? `${appDeepLinkBase}?token=${encodeURIComponent(token)}` : "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [invitationData, setInvitationData] = useState<ValidateInvitationResponseDTO | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    if (!token) {
      setErrorMessage("No se proporcionó un token de invitación");
      setPageState("error");
      return;
    }

    validateToken();
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    const timer = window.setTimeout(() => {
      window.location.href = appJoinUrl;
    }, 350);

    return () => window.clearTimeout(timer);
  }, [token, appJoinUrl]);

  const validateToken = async () => {
    if (!token) return;

    setPageState("loading");
    const response = await poolInvitationService.validate(token);

    if (response.success && response.data) {
      setInvitationData(response.data);
      setPageState("valid");
    } else {
      setErrorMessage(response.error || "El token de invitación no es válido");
      setPageState("error");
    }
  };

  const handleAcceptInvitation = async () => {
    if (!token) return;

    setPageState("accepting");
    const response = await poolInvitationService.accept({ token });

    if (response.success) {
      setPageState("success");
      message.success("¡Te has unido exitosamente a la polla!");
      setTimeout(() => {
        navigate(`/sports-predictions/pools/${invitationData?.poolId}`);
      }, 2000);
    } else {
      setErrorMessage(response.error || "Error al aceptar la invitación");
      setPageState("error");
    }
  };

  const handleRegisterWithToken = () => {
    navigate(`/register?token=${token}&email=${encodeURIComponent(invitationData?.emailInvited || "")}`);
  };

  const handleLoginWithToken = () => {
    navigate(`/login?token=${token}`);
  };

  if (pageState === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center">
        <div className="text-center">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "#10b981" }} spin />} />
          <p className="text-white mt-4 text-lg">Validando invitación...</p>
        </div>
      </div>
    );
  }

  if (pageState === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl">
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: "#ef4444" }} />}
            title={<span className="text-white">Invitación no válida</span>}
            subTitle={<span className="text-slate-400">{errorMessage}</span>}
            extra={
              <Link to="/login">
                <Button type="primary" className="bg-emerald-600 hover:bg-emerald-500 border-none">
                  Ir al inicio de sesión
                </Button>
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  if (pageState === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 shadow-2xl">
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: "#10b981" }} />}
            title={<span className="text-white">¡Bienvenido a la polla!</span>}
            subTitle={<span className="text-slate-400">Redirigiendo a {invitationData?.poolName}...</span>}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-lg w-full border border-slate-700/50 shadow-2xl">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🏆</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">¡Tienes una invitación!</h1>
          <p className="text-slate-400">
            <span className="text-emerald-400 font-semibold">{invitationData?.inviterName}</span> te ha invitado a unirte
          </p>
        </div>

        {/* Pool Card */}
        <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50 mb-8">
          <div className="text-center">
            <span className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">Polla</span>
            <h2 className="text-xl font-bold text-white mt-1">{invitationData?.poolName}</h2>
          </div>
        </div>

        {/* Email info */}
        <div className="bg-slate-900/30 rounded-lg p-4 mb-6 border border-slate-700/30">
          <p className="text-slate-400 text-sm text-center">
            Invitación enviada a: <span className="text-white font-medium">{invitationData?.emailInvited}</span>
          </p>
        </div>

        {/* Actions */}
        {isAuthenticated ? (
          <div className="space-y-4">
            {user?.email.toLowerCase() === invitationData?.emailInvited.toLowerCase() ? (
              <>
                <p className="text-slate-400 text-sm text-center mb-4">
                  ¡Hola <span className="text-emerald-400">{user?.firstName}</span>! Haz clic para unirte.
                </p>
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  loading={pageState === "accepting"}
                  onClick={handleAcceptInvitation}
                  className="h-14 bg-emerald-600 hover:bg-emerald-500 border-none text-lg font-semibold"
                >
                  {pageState === "accepting" ? "Uniéndote..." : "Unirme a la Polla"}
                </Button>
              </>
            ) : (
              <div className="text-center">
                <p className="text-amber-400 text-sm mb-4">
                  Estás logueado como <span className="font-semibold">{user?.email}</span>, pero esta invitación fue enviada a <span className="font-semibold">{invitationData?.emailInvited}</span>.
                </p>
                <p className="text-slate-400 text-sm">
                  Cierra sesión e ingresa con el correo correcto.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-slate-400 text-sm text-center mb-4">
              Para unirte a esta polla, necesitas una cuenta.
            </p>
            
            <Button
              type="primary"
              size="large"
              block
              icon={<UserAddOutlined />}
              onClick={handleRegisterWithToken}
              className="h-14 bg-emerald-600 hover:bg-emerald-500 border-none text-lg font-semibold"
            >
              Crear cuenta y unirme
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800/50 text-slate-500">o si ya tienes cuenta</span>
              </div>
            </div>
            
            <Button
              size="large"
              block
              onClick={handleLoginWithToken}
              className="h-12 bg-slate-700 hover:bg-slate-600 border-slate-600 text-white"
            >
              Iniciar sesión
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-700/50 text-center">
          <p className="text-slate-500 text-xs">
            Esta invitación expira en 7 días
          </p>
        </div>
      </div>
    </div>
  );
}
